"use client";

import Image from "next/image";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  itineraryCompactMeta,
  itineraryHasExpandedDetails,
  itineraryRouteLabel,
} from "@/lib/itinerary-day-fields";

export type ScrollItineraryStop = {
  stopId: string | number;
  dayLabel: string;
  title: string;
  body: string;
  image: string;
  stay?: string;
  meals?: string;
  driving?: string;
  activity?: string;
  elevation?: string;
  details?: string;
  startLocation?: string;
  endLocation?: string;
  route?: string;
  includedActivities?: string[];
  optionalActivities?: string[];
  highlights?: string[];
  notes?: string;
  tags?: string[];
};

type ScrollItineraryProps = {
  items: ScrollItineraryStop[];
  /** HTML data attribute name on each stop element (e.g. data-tdstop, data-stop) */
  dataAttribute?: string;
  /** How to label the sticky panel caption */
  labelMode?: "day" | "region-stop";
  panelHeight?: number;
  heading?: string;
  subtitle?: string;
  /** Per-day expand/collapse. Off for region guides that only have tags. */
  expandable?: boolean;
  showExpandAll?: boolean;
};

export function ScrollItinerary({
  items,
  dataAttribute = "data-tdstop",
  labelMode = "day",
  panelHeight = 480,
  heading,
  subtitle,
  expandable = false,
  showExpandAll = false,
}: ScrollItineraryProps) {
  const [activeId, setActiveId] = useState<string | number>(items[0]?.stopId ?? 1);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const baseId = useId();

  const active = useMemo(
    () => items.find((s) => String(s.stopId) === String(activeId)) ?? items[0],
    [activeId, items],
  );

  const expandableIds = useMemo(
    () => items.filter((item) => itineraryHasExpandedDetails(item)).map((item) => String(item.stopId)),
    [items],
  );
  const canExpandAny = expandable && expandableIds.length > 0;
  const allExpanded =
    expandableIds.length > 0 && expandableIds.every((id) => expandedIds.has(id));

  useEffect(() => {
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // A fast scroll (a trackpad fling, or a subnav tab jumping straight
        // to this section) can carry several stops across the narrow center
        // band between one observer check and the next, so one callback can
        // report multiple stops as newly intersecting together. Blindly
        // calling setActiveId for each in loop order left only the last one
        // in that batch active - producing "missing days in between" and
        // "jumps to the last one". Pick the single entry closest to the
        // viewport's vertical center instead, so a batch always resolves to
        // one correct stop rather than whichever happened to be observed last.
        const viewportCenter = window.innerHeight / 2;
        let closest: { id: string | number; distance: number } | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const raw = entry.target.getAttribute(dataAttribute);
          if (raw == null) continue;
          const parsed = /^\d+$/.test(raw) ? parseInt(raw, 10) : raw;
          const rectCenter = entry.boundingClientRect.top + entry.boundingClientRect.height / 2;
          const distance = Math.abs(rectCenter - viewportCenter);
          if (!closest || distance < closest.distance) closest = { id: parsed, distance };
        }
        if (closest) {
          const winner = closest.id;
          setActiveId((prev) => (String(prev) === String(winner) ? prev : winner));
        }
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );

    const timer = window.setTimeout(() => {
      document.querySelectorAll(`[${dataAttribute}]`).forEach((el) => {
        observerRef.current?.observe(el);
      });
    }, 200);

    return () => {
      window.clearTimeout(timer);
      observerRef.current?.disconnect();
    };
  }, [dataAttribute, items]);

  useEffect(() => {
    if (!expandable) return;

    const applyHash = () => {
      const match = window.location.hash.match(/^#day-(.+)$/);
      if (!match) return;
      const hashId = match[1];
      const item = items.find((stop) => String(stop.stopId) === hashId);
      if (!item) return;
      const id = String(item.stopId);
      if (itineraryHasExpandedDetails(item)) {
        setExpandedIds((prev) => {
          if (prev.has(id)) return prev;
          const next = new Set(prev);
          next.add(id);
          return next;
        });
      }
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.requestAnimationFrame(() => {
        document.getElementById(`day-${id}`)?.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "start",
        });
      });
    };

    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [expandable, items]);

  if (!active || items.length === 0) return null;

  const label =
    labelMode === "region-stop"
      ? `Stop ${active.stopId} of ${items.length} · ${active.dayLabel}`
      : `Day ${active.stopId} of ${items.length}`;

  const toggleDay = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setExpandedIds(allExpanded ? new Set() : new Set(expandableIds));
  };

  return (
    <div>
      {heading || (showExpandAll && canExpandAny) ? (
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
          <div>
            {heading ? (
              <h2 className="m-0 font-[family-name:var(--font-display)] text-[1.7rem] font-medium">
                {heading}
              </h2>
            ) : null}
            {subtitle ? (
              <p className="mt-3 mb-0 text-[14.5px] text-[var(--muted)]">{subtitle}</p>
            ) : null}
          </div>
          {showExpandAll && canExpandAny ? (
            <button
              type="button"
              onClick={toggleAll}
              className="inline-flex min-h-11 shrink-0 cursor-pointer items-center border-0 bg-transparent px-1 text-sm font-semibold text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="pbd-itin mt-7 grid items-start gap-9">
        <div
          className="pbd-itin-sticky relative overflow-hidden rounded-xl border border-[var(--border-subtle)]"
          style={{ position: "sticky", top: 140, height: panelHeight }}
        >
          <Image
            key={active.image}
            src={active.image}
            alt={active.title}
            fill
            className="object-cover transition-opacity duration-400"
            sizes="(max-width:980px) 100vw, 40vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(3,12,9,0.9)] via-[rgba(3,12,9,0.05)] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 [text-shadow:0_1px_4px_rgba(3,12,9,0.9)]">
            <p className="m-0 text-[11px] font-bold tracking-[0.16em] uppercase text-[var(--accent)]">
              {label}
            </p>
            <h3 className="mt-1.5 mb-0 font-[family-name:var(--font-display)] text-[1.6rem] font-medium text-white">
              {active.title}
            </h3>
          </div>
          <div className="absolute top-[18px] right-[18px] flex flex-col gap-1.5">
            {items.map((s) => (
              <span
                key={String(s.stopId)}
                className="h-[9px] w-[9px] rounded-full border-[1.5px] border-[rgba(255,255,255,0.7)] transition-colors duration-300"
                style={{
                  background:
                    String(s.stopId) === String(activeId)
                      ? "var(--accent)"
                      : "rgba(255,255,255,0.3)",
                }}
              />
            ))}
          </div>
        </div>

        <div className="flex min-w-0 flex-col">
          {items.map((stop) => {
            const id = String(stop.stopId);
            const open = expandedIds.has(id);
            const panelId = `${baseId}-day-${id}-details`;
            const buttonId = `${baseId}-day-${id}-toggle`;
            const compactMeta = itineraryCompactMeta(stop);
            const hasExtraDetails = itineraryHasExpandedDetails(stop);

            return (
              <div
                key={id}
                id={expandable ? `day-${id}` : undefined}
                {...{ [dataAttribute]: stop.stopId }}
                className={`relative min-w-0 border-l border-[var(--border-subtle)] pb-8 pl-9 ${
                  expandable ? "pbd-anchor" : ""
                }`}
              >
                {dataAttribute === "data-tdstop" ? (
                  <span className="absolute -left-[15px] top-[-2px] inline-flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[var(--accent)] bg-[var(--background)] font-[family-name:var(--font-display)] text-[0.95rem] text-[var(--accent)]">
                    {stop.stopId}
                  </span>
                ) : (
                  <span className="absolute -left-[9px] top-0.5 h-[18px] w-[18px] rounded-full border-[3px] border-[var(--background)] bg-[var(--accent)]" />
                )}
                {dataAttribute === "data-stop" ? (
                  <p className="m-0 text-[11px] font-bold tracking-[0.16em] uppercase text-[var(--dot)]">
                    {stop.dayLabel}
                  </p>
                ) : null}
                <h4
                  className={`m-0 font-[family-name:var(--font-display)] font-medium text-[var(--foreground)] ${
                    dataAttribute === "data-stop" ? "mt-1.5 text-[1.5rem]" : "text-[1.22rem]"
                  }`}
                >
                  {stop.title}
                </h4>
                {/* The day's own photo. Hidden on desktop, where the sticky
                    panel beside the list already shows it. On one column that
                    panel has nothing to sit beside: it rendered above the list
                    repeating the active day's title, so "Travel to trailhead"
                    appeared twice, overlapping. The photo belongs to the day,
                    so on mobile it moves into the day. */}
                <div className="pbd-itin-card-media relative mt-3 aspect-[16/10] overflow-hidden rounded-lg border border-[var(--border-subtle)]">
                  <Image
                    src={stop.image}
                    alt={stop.title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 980px) 100vw, 0px"
                  />
                </div>
                <p className="mt-2 mb-0 text-sm leading-[1.7] text-[var(--muted)]">{stop.body}</p>
                {!expandable && compactMeta ? (
                  <p className="mt-2 mb-0 text-[12.5px] tracking-[0.01em] text-[color-mix(in_srgb,var(--muted)_88%,var(--accent)_12%)]">
                    {compactMeta}
                  </p>
                ) : null}
                {stop.stay || stop.meals ? (
                  <dl className="mt-3 mb-0 grid gap-x-4 gap-y-1 text-sm text-[var(--muted)] sm:grid-cols-2">
                    {stop.stay ? (
                      <div>
                        <dt className="inline font-semibold text-[var(--accent)]">Accommodation: </dt>
                        <dd className="inline m-0">{stop.stay}</dd>
                      </div>
                    ) : null}
                    {stop.meals ? (
                      <div>
                        <dt className="inline font-semibold text-[var(--accent)]">Meals: </dt>
                        <dd className="inline m-0">{stop.meals}</dd>
                      </div>
                    ) : null}
                  </dl>
                ) : null}

                {expandable && hasExtraDetails ? (
                  <div className="mt-2">
                    <button
                      type="button"
                      id={buttonId}
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={() => toggleDay(id)}
                      className="inline-flex min-h-11 cursor-pointer items-center border-0 bg-transparent p-0 text-sm font-semibold text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
                    >
                      {open ? "Hide day details ↑" : "View day details ↓"}
                    </button>
                    {open ? (
                      <div
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        className="pbd-itin-details"
                        data-open="true"
                      >
                        <div className="pbd-itin-details-inner">
                          <DayDetailsPanel stop={stop} />
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {stop.tags?.length ? (
                  <div className="mt-3.5 flex flex-wrap gap-2">
                    {stop.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full border border-[var(--border-subtle)] px-2.5 py-1 text-[11px] text-[var(--muted)]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DayDetailsPanel({ stop }: { stop: ScrollItineraryStop }) {
  const route = itineraryRouteLabel(stop);
  const journey =
    stop.details && stop.details.trim() !== stop.body.trim() ? stop.details : undefined;
  const paragraphs = journey
    ? journey.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean)
    : [];
  const metaItems = [
    route ? { label: "Route", value: route } : null,
    stop.driving ? { label: "Driving", value: stop.driving } : null,
    stop.activity ? { label: "Activity", value: stop.activity } : null,
    stop.elevation ? { label: "Elevation", value: stop.elevation } : null,
  ].filter((item): item is { label: string; value: string } => item !== null);

  return (
    <div className="pbd-itin-day-panel">
      {paragraphs.length > 0 ? (
        <section>
          <h5 className="pbd-itin-day-label">Today&apos;s journey</h5>
          {paragraphs.map((paragraph) => (
            <p key={paragraph} className="mt-0 mb-3 last:mb-0 text-sm leading-[1.75] text-[var(--muted)]">
              {paragraph}
            </p>
          ))}
        </section>
      ) : null}

      {metaItems.length > 0 ? (
        <dl className="pbd-itin-day-meta">
          {metaItems.map((item) => (
            <div key={item.label} className="min-w-0">
              <dt className="pbd-itin-day-label">{item.label}</dt>
              <dd className="m-0 text-sm leading-[1.55] text-[var(--foreground)]">{item.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {stop.includedActivities?.length ? (
        <section>
          <h5 className="pbd-itin-day-label">Included</h5>
          <ul className="m-0 list-none p-0">
            {stop.includedActivities.map((item) => (
              <li key={item} className="flex gap-2 text-sm leading-[1.65] text-[var(--foreground)]">
                <span className="mt-px text-[var(--accent)]" aria-hidden="true">
                  ✓
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {stop.optionalActivities?.length ? (
        <section>
          <h5 className="pbd-itin-day-label">Optional</h5>
          <ul className="mb-0 mt-0 pl-[1.1em] text-sm leading-[1.65] text-[var(--foreground)]">
            {stop.optionalActivities.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {stop.highlights?.length ? (
        <section>
          <h5 className="pbd-itin-day-label">Highlights</h5>
          <p className="m-0 text-sm leading-[1.65] text-[var(--foreground)]">
            {stop.highlights.join(" · ")}
          </p>
        </section>
      ) : null}

      {stop.notes ? (
        <section>
          <h5 className="pbd-itin-day-label">Important notes</h5>
          <p className="m-0 text-sm leading-[1.7] text-[var(--muted)]">{stop.notes}</p>
        </section>
      ) : null}
    </div>
  );
}
